// Step 1 (Supplier selection) of CallOffWizard.tsx, split out to keep
// CallOffWizard.tsx under the 500-line cap. No behaviour change from the
// original inline JSX — moved verbatim.
import { ChevronDown, X } from 'lucide-react';
import { Label } from '../../ui/label';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import type { Supplier, SupplierStat } from './callOffWizardHelpers';

interface CallOffWizardSupplierStepProps {
  loadingSuppliers: boolean;
  selectedSupplier: Supplier | null;
  setSelectedSupplier: (supplier: Supplier | null) => void;
  supplierSearch: string;
  setSupplierSearch: (value: string) => void;
  supplierDropdownOpen: boolean;
  setSupplierDropdownOpen: (open: boolean) => void;
  filteredSuppliers: Supplier[];
  handleSupplierChange: (supplierId: string) => void;
  generatedReference: string;
  selectedSupplierStat: SupplierStat | null;
}

export default function CallOffWizardSupplierStep({
  loadingSuppliers,
  selectedSupplier,
  setSelectedSupplier,
  supplierSearch,
  setSupplierSearch,
  supplierDropdownOpen,
  setSupplierDropdownOpen,
  filteredSuppliers,
  handleSupplierChange,
  generatedReference,
  selectedSupplierStat,
}: CallOffWizardSupplierStepProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Supplier</Label>
        <div className="relative">
          <div className="relative">
            <input
              type="text"
              placeholder={loadingSuppliers ? 'Loading...' : 'Search suppliers...'}
              value={selectedSupplier ? selectedSupplier.name : supplierSearch}
              onChange={(e) => {
                setSupplierSearch(e.target.value);
                setSelectedSupplier(null);
                setSupplierDropdownOpen(true);
              }}
              onFocus={() => setSupplierDropdownOpen(true)}
              className="w-full px-3 py-2 pr-8 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            {selectedSupplier && (
              <button
                type="button"
                onClick={() => {
                  setSelectedSupplier(null);
                  setSupplierSearch('');
                }}
                className="absolute right-8 top-2 p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {supplierDropdownOpen && filteredSuppliers.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
              {filteredSuppliers.slice(0, 50).map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    handleSupplierChange(String(s.id));
                    setSupplierSearch('');
                    setSupplierDropdownOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          {supplierDropdownOpen && (
            <div
              className="fixed inset-0 z-40"
              onClick={() => setSupplierDropdownOpen(false)}
            />
          )}
        </div>
      </div>

      {selectedSupplier && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Supplier Code</span>
              <span className="font-mono text-sm">{selectedSupplier.supplier_code}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Reference Preview</span>
              <span className="font-mono text-sm text-muted-foreground">{generatedReference}</span>
            </div>
            {selectedSupplierStat && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Total Call-Offs</span>
                  <Badge variant="secondary">{selectedSupplierStat.total}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Active</span>
                  <Badge variant="secondary">{selectedSupplierStat.active}</Badge>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
